import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React from 'react';
import { GRADIENT_PRESETS } from '../../constants';
import {
  VerticalDivider,
  RingMetricsContainer,
  StyledSkeletonBox,
  ProgressRingWrapper,
  MetricBoxWrapper,
  MetricValue,
  StyledSection,
  SkeletonBox,
  MetricLabel
} from '../../RegistryGiftAdvisor.styles';
import { GuestPreferenceMessage } from '../GuestPreferenceMessage/GuestPreferenceMessage';
import { ProgressRing } from '../ProgressRing';
import { ReactComponent as GiftIcon } from '@assets/icons/add-gift.svg';
import { ReactComponent as Check } from '@assets/icons/check-round-gray.svg';

interface AdvisorMetricsProps {
  everything?: number;
  purchased?: number;
  getResultsLoading: boolean;
  handleOnGiftTypeClick: (event: React.SyntheticEvent<HTMLDivElement>) => void;
  giftsAvailable: () => string;
  reservedAndPurchased: () => string;
  completionPercent: number;
  formattedCompletionPercent: number;
  giftsCompleted: () => string;
  giftCount: (params: { count: number }) => string;
  targetGiftCount: number;
  cashFundCompletionPercent: number;
  cashFundTotalCount: number;
  cashFundCompleted: () => string;
  cashFundCount: () => string;
  messageKeys: Array<{ titleKey: string; subtitleKey: string }>;
  initialMessage: string;
  isMobile?: boolean;
  isTabletScreen?: boolean;
  onActionComplete?: () => void;
}

const RING_DIMENSIONS = {
  width: 52,
  height: 52,
  cx: 26,
  cy: 26,
  r: 23,
  strokeWidth: 4.65
};

const ProgressRingBox: React.FC<{
  percent: number;
  count?: number;
  label: string;
  color: string;
  progressColor: string;
  gradientColors?: typeof GRADIENT_PRESETS.TEAL;
  isLoading?: boolean;
}> = ({ percent, count, label, color, progressColor, gradientColors, isLoading }) => (
  <ProgressRingWrapper padding={6}>
    {isLoading ? (
      <>
        <StyledSkeletonBox width={pxToRem(52)} height={pxToRem(52)} borderRadius="50%" />
        <StyledSkeletonBox width={pxToRem(80)} height={pxToRem(16)} marginTop={6} />
      </>
    ) : (
      <>
        <ProgressRing
          progressPercent={percent}
          isOpen
          showCheck={true}
          dimensions={RING_DIMENSIONS}
          currentGiftCount={count}
          countTextColor={color}
          progressColor={progressColor}
          gradientColors={gradientColors}
          useGradient={!!gradientColors}
        />
        <MetricLabel
          marginTop={6}
          textAlign="center"
          dangerouslySetInnerHTML={{
            __html: label || ''
          }}
        />
      </>
    )}
  </ProgressRingWrapper>
);

const MetricBox = ({
  value,
  label,
  isLoading,
  dataArea,
  handleGiftTypeClick
}: {
  value?: number;
  label: () => string;
  isLoading?: boolean;
  dataArea: string;
  handleGiftTypeClick: (event: React.SyntheticEvent<HTMLDivElement>) => void;
}) => (
  <MetricBoxWrapper data-area={dataArea} onClick={handleGiftTypeClick} padding={6}>
    <MetricValue>{isLoading ? <StyledSkeletonBox width="50px" height="36px" /> : value}</MetricValue>
    <MetricLabel
      typographyVariant="body1"
      dangerouslySetInnerHTML={{
        __html: label()
      }}
    />
  </MetricBoxWrapper>
);

const AdvisorMetrics: React.FC<AdvisorMetricsProps> = ({
  everything,
  purchased,
  getResultsLoading,
  handleOnGiftTypeClick,
  giftsAvailable,
  reservedAndPurchased,
  completionPercent,
  formattedCompletionPercent,
  giftsCompleted,
  giftCount,
  targetGiftCount,
  cashFundCompletionPercent,
  cashFundTotalCount,
  cashFundCompleted,
  cashFundCount,
  messageKeys,
  initialMessage,
  isMobile = false,
  isTabletScreen = false,
  onActionComplete
}) => {
  if (isMobile) {
    return (
      <Box>
        <Flex justifyContent="space-between" position="relative" borderBottom="1px solid" borderColor="mono3">
          <MetricBox
            value={everything}
            label={giftsAvailable}
            isLoading={getResultsLoading}
            dataArea="available"
            handleGiftTypeClick={e => {
              onActionComplete?.();
              handleOnGiftTypeClick(e);
            }}
          />
          <VerticalDivider />
          <MetricBox
            value={purchased}
            label={reservedAndPurchased}
            isLoading={getResultsLoading}
            dataArea="purchased"
            handleGiftTypeClick={e => {
              onActionComplete?.();
              handleOnGiftTypeClick(e);
            }}
          />
        </Flex>
        <Box display="flex" flexDirection="row" padding={6} minHeight={pxToRem(129)} height="auto" borderBottom="1px solid" borderColor="mono3">
          <GuestPreferenceMessage isMobile messageKeys={messageKeys} containerHeight={pxToRem(129)} initialMessage={initialMessage} />
        </Box>
        <RingMetricsContainer>
          <ProgressRingBox
            percent={completionPercent}
            count={everything}
            label={formattedCompletionPercent === 100 ? giftsCompleted() : giftCount({ count: targetGiftCount })}
            color="#4751F8"
            progressColor="color(display-p3 0.3059 0.0235 0.949)"
            isLoading={getResultsLoading}
          />
          <VerticalDivider />
          <ProgressRingBox
            percent={cashFundCompletionPercent}
            count={cashFundTotalCount}
            label={cashFundCompletionPercent === 100 ? cashFundCompleted() : cashFundCount()}
            color="#00BAAC"
            progressColor="color(display-p3 0.0941 0.5725 0.5373)"
            gradientColors={GRADIENT_PRESETS.TEAL}
            isLoading={getResultsLoading}
          />
        </RingMetricsContainer>
      </Box>
    );
  } else {
    return (
      <Box flexDirection="row" display="flex" minHeight={!isTabletScreen ? '168px' : ''} height="auto">
        <StyledSection width={isTabletScreen ? '25%' : pxToRem(172)} padding={6} data-area="available" onClick={handleOnGiftTypeClick}>
          <Flex flexDirection="column" justifyContent="space-between" height="100%">
            <TextV2 fontSize={pxToRem(48)} color="#4751F8" fontWeight={400} lineHeight={pxToRem(48)}>
              {getResultsLoading ? <SkeletonBox width={pxToRem(60)} height={pxToRem(48)} /> : everything}
            </TextV2>
            <TextV2
              fontSize={pxToRem(15)}
              color="mono12"
              fontWeight={500}
              maxWidth={pxToRem(80)}
              __css={{ textAlign: 'left' }}
              dangerouslySetInnerHTML={{
                __html: giftsAvailable()
              }}
            />
          </Flex>
          <Flex alignSelf="flex-start" __css={{ 'svg path': { fill: '#949494' } }}>
            <GiftIcon />
          </Flex>
        </StyledSection>

        <StyledSection width={isTabletScreen ? '25%' : pxToRem(178)} padding={6} data-area="purchased" onClick={handleOnGiftTypeClick}>
          <Flex flexDirection="column" justifyContent="space-between" height="100%">
            <TextV2 fontSize={pxToRem(48)} color="#4751F8" fontWeight={400} lineHeight={pxToRem(48)}>
              {getResultsLoading ? <SkeletonBox width={pxToRem(60)} height={pxToRem(48)} /> : purchased}
            </TextV2>
            <TextV2
              fontSize={pxToRem(15)}
              color="mono12"
              fontWeight={500}
              dangerouslySetInnerHTML={{
                __html: reservedAndPurchased()
              }}
            />
          </Flex>
          <Flex alignSelf="flex-start" __css={{ svg: { width: pxToRem(20), height: pxToRem(20) }, 'svg path': { fill: '#949494 !important' } }}>
            <Check />
          </Flex>
        </StyledSection>

        <Flex borderRight="1px solid" borderColor="mono3" flexDirection="column" width={isTabletScreen ? '25%' : pxToRem(148)} padding={6} justifyContent="space-between">
          {getResultsLoading ? (
            <>
              <SkeletonBox width={pxToRem(62)} height={pxToRem(62)} borderRadius="50%" marginBottom={5} />
              <SkeletonBox width={pxToRem(80)} height={pxToRem(15)} />
            </>
          ) : (
            <>
              <ProgressRing
                progressPercent={completionPercent}
                isOpen={true}
                showCheck={true}
                dimensions={{
                  width: 62,
                  height: 62,
                  cx: 31,
                  cy: 31,
                  r: 28,
                  strokeWidth: 5.65
                }}
                currentGiftCount={everything}
                countTextColor="#4751F8"
                progressColor="color(display-p3 0.3059 0.0235 0.949)"
                useGradient={true}
              />
              <TextV2
                fontSize={pxToRem(15)}
                color="mono12"
                fontWeight={500}
                dangerouslySetInnerHTML={{
                  __html: formattedCompletionPercent === 100 ? giftsCompleted() : giftCount({ count: targetGiftCount })
                }}
              />
            </>
          )}
        </Flex>

        <Flex
          borderRight={!isTabletScreen ? '1px solid' : 'none'}
          borderColor="mono3"
          flexDirection="column"
          width={isTabletScreen ? '25%' : pxToRem(148)}
          paddingX={pxToRem(22)}
          paddingY={6}
          justifyContent="space-between"
        >
          {getResultsLoading ? (
            <>
              <SkeletonBox width={pxToRem(62)} height={pxToRem(62)} borderRadius="50%" marginBottom={5} />
              <SkeletonBox width={pxToRem(80)} height={pxToRem(15)} />
            </>
          ) : (
            <>
              <ProgressRing
                progressPercent={cashFundCompletionPercent}
                isOpen={true}
                showCheck={true}
                dimensions={{
                  width: 62,
                  height: 62,
                  cx: 31,
                  cy: 31,
                  r: 28,
                  strokeWidth: 5.65
                }}
                currentGiftCount={cashFundTotalCount}
                countTextColor="#00BAAC"
                progressColor="color(display-p3 0.0941 0.5725 0.5373)"
                gradientColors={GRADIENT_PRESETS.TEAL}
                useGradient={true}
              />
              <TextV2
                fontSize={pxToRem(15)}
                color="mono12"
                fontWeight={500}
                dangerouslySetInnerHTML={{
                  __html: cashFundCompletionPercent === 100 ? cashFundCompleted() : cashFundCount()
                }}
              />
            </>
          )}
        </Flex>

        {!isTabletScreen && (
          <Flex flexDirection="column" paddingX={7} paddingY={6} width="50%">
            <GuestPreferenceMessage messageKeys={messageKeys} containerHeight={pxToRem(145)} initialMessage={initialMessage} />
          </Flex>
        )}
      </Box>
    );
  }
};

export default AdvisorMetrics;
